var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import useForkRef from '@mui/utils/useForkRef';
import clsx from 'clsx';
import { useCardLayout } from '../useCardLayout';
import { useDebouncedResizeObserver } from '../useDebouncedResizeObserver';
const StyledRoot = styled(Box) `
    position: relative;
    width: 100%;
    height: 100%;
`;
const StyledButtonContainer = styled(Box) `
    position: absolute;
    z-index: 100;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-flow: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
`;
const StyledPageButton = styled(IconButton) `
    &:hover {
        background-color: ${({ theme }) => theme.palette.primary.background};
    }
`;
const StyledPrevContainer = styled(StyledButtonContainer) `
    left: 0;
`;
const StyledNextContainer = styled(StyledButtonContainer) `
    right: 0;
`;
const StyledPageNumber = styled(Box) `
    font-size: 80%;
`;
export const GalleryView = forwardRef((props, ref) => {
    var _a, _b;
    const { cardAspectRatio = 16 / 9, hidePagination = false, hidePageNumbers = false, texts = {}, maxItemsPerPage, minCardWidth, nextButtonProps, padding = 4, previousButtonProps, children, onStartIndexUpdated, onCardsUpdated, startIndex: propsStartIndex, innerContainerRef, innerContainerExtraContent, debounceTimer = 0, uniqueIds, spotlightUniqueId } = props, boxProps = __rest(props, ["cardAspectRatio", "hidePagination", "hidePageNumbers", "texts", "maxItemsPerPage", "minCardWidth", "nextButtonProps", "padding", "previousButtonProps", "children", "onStartIndexUpdated", "onCardsUpdated", "startIndex", "innerContainerRef", "innerContainerExtraContent", "debounceTimer", "uniqueIds", "spotlightUniqueId"]);
    const { ref: innerRef, width: containerWidth = 1, height: containerHeight = 1 } = useDebouncedResizeObserver(debounceTimer, undefined, {
        leading: true
    });
    const combinedRef = useForkRef(innerRef, innerContainerRef);
    const [startIndexInternal, setStartIndexInternal] = useState(0);
    const startIndex = propsStartIndex !== null && propsStartIndex !== void 0 ? propsStartIndex : startIndexInternal;
    const itemsPerPage = Math.min(maxItemsPerPage, uniqueIds.length);
    const options = {
        cardAspectRatio,
        cardCountLimit: itemsPerPage,
        minCardWidth: minCardWidth,
        padding
    };
    const { cards, maxPossibleCards, allZoomSizes, currentZoomSizeIndex } = useCardLayout(containerWidth, containerHeight, options);
    let items = uniqueIds.slice(startIndex, startIndex + maxPossibleCards);
    const spotlightIndex = uniqueIds.findIndex((i) => i === spotlightUniqueId);
    const hasSpotlight = spotlightIndex !== -1;
    const spotlightMovedToCurrentPage = hasSpotlight && !items.some((i) => i === spotlightUniqueId);
    const nextPageFormat = (_a = texts.nextPageFormat) !== null && _a !== void 0 ? _a : ((next, total) => `${next}/${total}`);
    const prevPageFormat = (_b = texts.prevPageFormat) !== null && _b !== void 0 ? _b : ((prev, total) => `${prev}/${total}`);
    if (spotlightMovedToCurrentPage) {
        items = [spotlightUniqueId, ...items].slice(0, maxPossibleCards);
    }
    const endingIndex = uniqueIds.findIndex((item) => {
        return item === items[items.length - 1];
    });
    const updateStartIndex = useCallback((newStartIndex) => {
        if (propsStartIndex !== undefined) {
            onStartIndexUpdated === null || onStartIndexUpdated === void 0 ? void 0 : onStartIndexUpdated(newStartIndex);
            return;
        }
        setStartIndexInternal(newStartIndex);
    }, [onStartIndexUpdated, propsStartIndex]);
    // $20 to anyone that figures out a better way that takes spotlight into account
    const { totalPages, currentPage } = useMemo(() => {
        if (hidePageNumbers) {
            // optimize by ignoring the logic if page numbers are hidden anyway
            return { totalPages: 1, currentPage: 1 };
        }
        let calculatedPages = 0;
        let calculatedCurrentPage = 0;
        let currentIndex = 0;
        if (maxPossibleCards === 1) {
            return {
                currentPage: startIndex + 1,
                totalPages: uniqueIds.length
            };
        }
        // simulate each page
        while (currentIndex < uniqueIds.length) {
            calculatedPages += 1;
            // check for 0 to make sure this only happens once (we count from start so we're ok)
            if (calculatedCurrentPage === 0 && currentIndex >= startIndex) {
                calculatedCurrentPage = calculatedPages;
            }
            if (spotlightIndex !== -1) {
                // if spotlight is on this page then don't substract -1 as no element is being replaced
                if (spotlightIndex >= currentIndex && spotlightIndex < currentIndex + maxPossibleCards) {
                    currentIndex += maxPossibleCards;
                }
                else {
                    currentIndex += maxPossibleCards - 1;
                }
            }
            else {
                currentIndex += maxPossibleCards;
            }
        }
        return { totalPages: calculatedPages, currentPage: calculatedCurrentPage };
    }, [uniqueIds, spotlightIndex, startIndex, maxPossibleCards, hidePageNumbers]);
    const onPreviousClick = useCallback(() => {
        const previousPageStart = startIndex - maxPossibleCards;
        const spotlightOnPreviousPage = spotlightIndex >= previousPageStart && spotlightIndex < startIndex;
        const newStartIndex = previousPageStart + (spotlightIndex === -1 || spotlightOnPreviousPage ? 0 : 1);
        const withLimit = Math.max(0, newStartIndex);
        updateStartIndex(withLimit);
    }, [maxPossibleCards, startIndex, spotlightIndex, updateStartIndex]);
    let lastPageThreshold = uniqueIds.length - maxPossibleCards;
    if (hasSpotlight && spotlightIndex < lastPageThreshold) {
        lastPageThreshold += 1;
    }
    const onNextClick = useCallback(() => {
        const withLimit = Math.min(endingIndex + 1, lastPageThreshold);
        updateStartIndex(withLimit);
    }, [endingIndex, lastPageThreshold, updateStartIndex]);
    // when the last page is missing items due to items getting removed, fill missing back in
    useEffect(() => {
        if (startIndex > lastPageThreshold) {
            updateStartIndex(Math.max(lastPageThreshold, 0));
        }
    }, [startIndex, lastPageThreshold, updateStartIndex]);
    useEffect(() => {
        window.setTimeout(() => {
            onCardsUpdated === null || onCardsUpdated === void 0 ? void 0 : onCardsUpdated(cards);
        });
    }, [cards, onCardsUpdated]);
    const displayPrevButton = startIndex > 0;
    const displayNextButton = endingIndex < uniqueIds.length - 1;
    const paginationButtonDisplayed = displayPrevButton || displayNextButton;
    return (_jsxs(StyledRoot, Object.assign({ className: 'ClassGalleryView-root' }, boxProps, { ref: ref, children: [_jsxs(Box, { className: 'ClassGalleryView-container', sx: {
                    position: 'absolute',
                    width: paginationButtonDisplayed ? 'calc(100% - 88px)' : '100%',
                    height: '100%',
                    left: '50%',
                    transform: 'translate(-50%)'
                }, ref: combinedRef, children: [innerContainerExtraContent, cards.map((card, i) => {
                        // move the index by 1 to the left if the spotlighted video got pushed in front because it was not visible
                        const key = items[i];
                        if (!key) {
                            console.info('Could not find key for item', { index: i, itemsLength: items.length });
                            return null;
                        }
                        const isSpotlighted = key === spotlightUniqueId;
                        return (
                        // Using div instead of Box so MUI is not complaining about missing sx props being created multiple times
                        _jsx("div", { style: {
                                width: card.width,
                                height: card.height,
                                left: card.start,
                                top: card.top,
                                position: 'absolute'
                            }, 
                            // TODO: Allow both Class and ClassUi prefix - deprecate ClassUi for the next major release
                            className: clsx('ClassGalleryView-card', 'ClassUiGalleryView-card', isSpotlighted && 'ClassGalleryView-cardSpotlight'), children: children(Object.assign({ cardKey: key, cols: allZoomSizes[currentZoomSizeIndex !== null && currentZoomSizeIndex !== void 0 ? currentZoomSizeIndex : 0].cols, rows: allZoomSizes[currentZoomSizeIndex !== null && currentZoomSizeIndex !== void 0 ? currentZoomSizeIndex : 0].rows, isSpotlighted }, card)) }, key));
                    })] }), !hidePagination && displayPrevButton && (_jsxs(StyledPrevContainer, { children: [_jsx(StyledPageButton, Object.assign({ className: 'ClassGalleryView-pagePrev', "data-testid": 'pagination-previous', onClick: onPreviousClick }, previousButtonProps, { children: _jsx(ArrowBackIosIcon, {}) })), !hidePageNumbers && (_jsx(StyledPageNumber, { children: prevPageFormat(currentPage - 1, totalPages) }))] })), !hidePagination && displayNextButton && (_jsxs(StyledNextContainer, { children: [_jsx(StyledPageButton, Object.assign({ className: 'ClassGalleryView-pageNext', "data-testid": 'pagination-next', onClick: onNextClick }, nextButtonProps, { children: _jsx(ArrowForwardIosIcon, {}) })), !hidePageNumbers && (_jsx(StyledPageNumber, { children: nextPageFormat(currentPage + 1, totalPages) }))] }))] })));
});
